import { CustomMutatorDefs, Transaction, UpdateValue } from "@rocicorp/zero";

import { AuthData, Schema } from "../server/zero-schema";
import { Task } from "../types";

async function assertHasBoardAccess(
  authData: AuthData,
  tx: Transaction<Schema>,
  boardId: string,
) {
  const board = await tx.query.boardMemberships
    .where("boardId", "=", boardId)
    .where("userId", "=", authData.sub)
    .one()
    .run();
  if (!board) {
    throw new Error(`Board ${boardId} not found`);
  }
}

async function assertHasBoardAdminAccess(
  authData: AuthData,
  tx: Transaction<Schema>,
  boardId: string,
) {
  const board = await tx.query.boardMemberships
    .where("boardId", "=", boardId)
    .where("userId", "=", authData.sub)
    .where("role", "=", "admin")
    .one()
    .run();
  if (!board) {
    throw new Error(`Board ${boardId} not found`);
  }
}

async function assertHasListAccess(
  authData: AuthData,
  tx: Transaction<Schema>,
  boardId: string,
  listId: string,
) {
  const list = await tx.query.lists
    .where("id", "=", listId)
    .whereExists("board", (q) =>
      q.whereExists("memberships", (q) =>
        q.where("userId", authData.sub).where("boardId", boardId),
      ),
    )
    .one()
    .run();
  if (!list) {
    throw new Error(`List ${listId} not found`);
  }
}

async function assertCanEditTask(
  authData: AuthData,
  tx: Transaction<Schema>,
  taskId: string,
) {
  const task = await tx.query.tasks
    .where("id", "=", taskId)
    .whereExists("board", (q) =>
      q.whereExists("memberships", (q) => q.where("userId", authData.sub)),
    )
    .one()
    .run();
  if (!task) {
    throw new Error(`Task ${taskId} not found`);
  }
  return task;
}

export function createMutators(authData: AuthData) {
  return {
    tasks: {
      create: async (
        tx,
        data: Omit<Task, "createdAt" | "updatedAt" | "taskNumber">,
      ) => {
        // todo: schema validation

        if (tx.location === "server") {
          await assertHasBoardAccess(authData, tx, data.boardId);
          await assertHasListAccess(authData, tx, data.boardId, data.listId);
        }

        const lastTaskNumber = await tx.query.tasks
          .where("boardId", data.boardId)
          .orderBy("taskNumber", "desc")
          .limit(1)
          .one()
          .run()
          .then((t) => t?.taskNumber);

        const now = new Date().getTime();
        await tx.mutate.tasks.insert({
          ...data,
          createdAt: now,
          updatedAt: now,
          taskNumber: (lastTaskNumber ?? 0) + 1,
        });
      },
      update: async (tx, data: UpdateValue<Schema["tables"]["tasks"]>) => {
        if (tx.location === "server") {
          const task = await assertCanEditTask(authData, tx, data.id!);
          if (data.boardId) {
            await assertHasBoardAccess(authData, tx, data.boardId);
          }
          if (data.listId) {
            await assertHasListAccess(
              authData,
              tx,
              data.boardId ?? task.boardId,
              data.listId,
            );
          }
        }

        await tx.mutate.tasks.update({
          ...data,
          updatedAt: new Date().getTime(),
        });
      },
      delete: async (tx, data: { id: string }) => {
        if (tx.location === "server") {
          await assertCanEditTask(authData, tx, data.id);
        }
        // todo: soft delete
        await tx.mutate.tasks.delete(data);
      },
      addAssignee: async (tx, data: { taskId: string; userId: string }) => {
        if (tx.location === "server") {
          const task = await assertCanEditTask(authData, tx, data.taskId);
          const membership = await tx.query.boardMemberships
            .where("boardId", "=", task.boardId)
            .where("userId", "=", data.userId)
            .one()
            .run();
          if (!membership) {
            throw new Error(
              `The user ${data.userId} is not a member of the board ${task.boardId}`,
            );
          }
        }
        await tx.mutate.taskAssignments.insert(data);
      },
      removeAssignee: async (tx, data: { taskId: string; userId: string }) => {
        if (tx.location === "server") {
          await assertCanEditTask(authData, tx, data.taskId);
        }
        await tx.mutate.taskAssignments.delete({
          taskId: data.taskId,
          userId: data.userId,
        });
      },
    },
    boards: {
      update: async (tx, data: UpdateValue<Schema["tables"]["boards"]>) => {
        if (tx.location === "server") {
          await assertHasBoardAdminAccess(authData, tx, data.id!);
        }
        await tx.mutate.boards.update(data);
      },
    },
  } as const satisfies CustomMutatorDefs<Schema>;
}
